import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Camera, Mic, ShieldCheck as ShieldCheck2, ClipboardCheck, ListOrdered, Brain } from "lucide-react";
import Navbar from "@/components/Navbar";
import Logo from "@/components/Logo";
import FeatureShowcase from "@/components/FeatureShowcase";
import ForensicsFAQ from "@/components/ForensicsFAQ";
import HeroPhoneMockup from "@/components/HeroPhoneMockup";
import SiteFooter from "@/components/SiteFooter";
import { BALANCE_SHIFTED, START_TODAY, BUILT_BY_RENTER, COMPASS_STRATEGIST } from "@/data/landingCopy";

const Pillar = ({ Icon, label, body }) => (
 <div className="flex flex-col items-start gap-3 px-4 py-6 md:px-6 md:py-8 border-r border-[#1B6C62]/15 last:border-r-0" data-testid={`pillar-${label.toLowerCase()}`}>
 <Icon size={36} strokeWidth={1.25} className="text-[#1B6C62]" />
 <div className="font-mono text-sm tracking-[0.2em] text-[#5EE0CC]">{label}</div>
 <p className="text-sm text-white/75 leading-relaxed">{body}</p>
 </div>
);

export default function Landing() {
 return (
 <div className="min-h-screen paper-grain">
 <Navbar />

 {/* HERO */}
 <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-12 pb-14 lg:pt-16 lg:pb-20 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
 <div className="lg:col-span-7 fade-up">
          <div className="inline-flex items-center gap-3 mb-6" data-testid="hero-brand-lockup">
            <Logo size={28} />
            <span className="font-serif text-[15px] text-[#0D2B52] tracking-tight">TenantTrail</span>
            <span className="hidden sm:inline-block h-3 w-px bg-[#0D2B52]/25" aria-hidden="true"></span>
            <span className="label-mono">Protection &middot; Evidence &middot; Accountability</span>
          </div>
 <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0D2B52] tracking-tight leading-[1.05] mb-5">
 Your real estate agent has a system.<br />
 <span className="text-[#1B6C62]">Now you do too.</span>
 </h1>
 <p className="text-lg text-[#333] leading-relaxed mb-6 max-w-xl">
 TenantTrail keeps every lease, email, photo and date on your side. Compass knows the laws, the acts and the codes that protect you, then tells you exactly how to respond. From your first inspection to your last bond dollar, push back on anything that isn&rsquo;t fair. Calmly, with the receipts to prove it.
 </p>
 <p className="font-serif text-[15px] sm:text-base text-[#0D2B52] leading-relaxed mb-6 max-w-xl border-l-2 border-[#1B6C62] pl-4" data-testid="hero-tagline">
 Most renters don&rsquo;t lose because they&rsquo;re in the wrong. They lose because they didn&rsquo;t know the rule, or couldn&rsquo;t prove what happened.
 </p>
 <div className="flex flex-wrap items-center gap-3 mb-5">
 <Link to="/register" className="btn-teal inline-flex items-center gap-2" data-testid="hero-cta-register">
 Start free, no card needed <ArrowRight size={16} strokeWidth={1.5} />
 </Link>
 <Link to="/pricing" className="btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-pricing">
 See plans
 </Link>
 </div>
 <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#6B7280] font-mono mb-4">
 <Link to="/q" className="hover:text-[#1B6C62]" data-testid="hero-link-qa">&rarr; Tenant questions, answered</Link>
 <Link to="/check" className="hover:text-[#1B6C62]" data-testid="hero-link-check">&rarr; Check a rental before you sign</Link>
 <Link to="/ask" className="hover:text-[#1B6C62]" data-testid="hero-link-brain">&rarr; Free chat with Compass</Link>
 </div>
 <p className="text-[10.5px] text-[#9CA3AF] max-w-xl italic mb-2">
 Made in Australia. Your documents stored in Australia, never sold, never used to train public AI.
 </p>
 <p className="text-[11px] text-[#9CA3AF] max-w-xl italic">
 Already in a dispute? <Link to="/pricing" className="underline hover:text-[#1B6C62] not-italic">Dispute Mode</Link>, $69 one-off, 30 days with a generous AI allowance for the tough stuff.
 </p>
 </div>

 <div className="lg:col-span-5 fade-up fade-up-1">
 <HeroPhoneMockup />
 </div>
 </section>

 {/* CAPABILITY BAR, six pillars on navy */}
 <section className="bg-[#0D2B52] text-white">
 <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-3">
 <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
 <Pillar Icon={FileText} label="STORE" body="Leases, bond receipts, agent emails, even vet records and vaccination certs. One safe place, so you never dig through your inbox again." />
 <Pillar Icon={Camera} label="CAPTURE" body="A two-minute photo round, once a month. Come move-out, you&rsquo;ve got dozens of dated photos. All they&rsquo;ve got is a hunch." />
 <Pillar Icon={Mic} label="RECORD" body="Voice-note what just happened. We keep the audio, you can transcribe it on demand. Share a tamper-evident link with a friend in one tap." />
 <Pillar Icon={ShieldCheck2} label="PROTECT" body="Compass reads your agent&rsquo;s emails and flags anything that isn&rsquo;t right, before it costs you." />
 <Pillar Icon={ClipboardCheck} label="PREPARE" body="If it ever goes to tribunal, your full evidence bundle is one tap away." />
 <Pillar Icon={ListOrdered} label="REMEMBER" body="Rent rises, lease endings, bond-lodgement deadlines. We watch the dates so you never miss one." />
 </div>
 </div>
 </section>

 {/* THE BALANCE JUST SHIFTED, verbatim owner copy from /data/landingCopy.js */}
 <section className="border-y border-[#E5E4DE] bg-white" data-testid="landing-balance-shifted">
 <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
 <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
 <div className="lg:col-span-7">
 <div className="label-mono mb-3 font-bold !text-[#0D2B52] text-[11.5px] sm:text-[12.5px]">{BALANCE_SHIFTED.label}</div>
 <h2 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] leading-[1.15] mb-7 max-w-xl">
 {BALANCE_SHIFTED.heading}
 </h2>
 <div className="font-serif text-[17px] sm:text-[18px] text-[#1A1A1A] leading-[1.7] space-y-5">
 {BALANCE_SHIFTED.paragraphs.map((p, i) => (
 <p key={i}>{p}</p>
 ))}
 </div>
 </div>
 <aside className="lg:col-span-5 lg:pt-14" data-testid="balance-shifted-pullquote">
 <figure className="bg-[#FAF7F0] border-l-4 border-l-[#1B6C62] px-6 py-6 lg:px-7 lg:py-7">
 <blockquote className="font-serif text-[19px] sm:text-[21px] text-[#0D2B52] leading-snug">
 &ldquo;Agents act differently the moment they know the renter has receipts.&rdquo;
 </blockquote>
 <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.14em] text-[#1B6C62]">
 Why it works
 </figcaption>
 </figure>
 </aside>
 </div>
 </div>
 </section>

 {/* HABIT / PROACTIVE ALERTS — removed. Its point (we watch the dates you would otherwise forget) is already covered by the REMEMBER pillar above. */}

 {/* MEET COMPASS moved up so the strongest proof-of-value moment lands early. */}
 <section className="bg-[#FAF7F0] border-y border-[#E5E4DE]" data-testid="landing-compass-strategist">
 <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-14">
 <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
 <div className="lg:col-span-6">
 <div className="inline-flex items-center gap-2 px-3 py-1 bg-white border border-[#1B6C62]/30 mb-4">
 <Brain size={14} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="font-mono text-[10px] tracking-wider uppercase text-[#0D2B52]">Meet Compass</span>
 </div>
 <h2 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] leading-tight mb-5">
 Your strategist in the corner.
 </h2>
 <div className="font-serif text-[16px] sm:text-[17px] text-[#1A1A1A] leading-relaxed space-y-4 mb-7">
 {COMPASS_STRATEGIST.paragraphs.map((p, i) => (
 <p key={i}>{p}</p>
 ))}
 </div>
 <div className="flex flex-wrap items-center gap-3 mb-3">
 <Link to={COMPASS_STRATEGIST.primaryCtaHref} className="btn-teal inline-flex items-center gap-2" data-testid="compass-primary-cta">
 {COMPASS_STRATEGIST.primaryCtaLabel} <ArrowRight size={16} strokeWidth={1.5} />
 </Link>
 <Link to={COMPASS_STRATEGIST.secondaryCtaHref} className="btn-secondary inline-flex items-center gap-2" data-testid="compass-secondary-cta">
 {COMPASS_STRATEGIST.secondaryCtaLabel}
 </Link>
 </div>
 <p className="text-[12px] text-[#6B7280] italic font-serif">
 {COMPASS_STRATEGIST.reassurance}
 </p>
 </div>

 {/* Dialogue card (right column on desktop, stacked on mobile) */}
 <div className="lg:col-span-6 lg:pt-16">
 <div className="card border-l-4 border-l-[#1B6C62]" data-testid="compass-dialogue">
 <div className="mb-4">
 <div className="label-mono mb-1.5 text-[#0D2B52]">{COMPASS_STRATEGIST.dialogue.youLabel}</div>
 <p className="text-sm font-serif text-[#1A1A1A] leading-relaxed italic">
 &ldquo;{COMPASS_STRATEGIST.dialogue.you}&rdquo;
 </p>
 </div>
 <div className="border-t border-[#E5E4DE] pt-4">
 <div className="flex items-center gap-2 mb-1.5">
 <Brain size={12} strokeWidth={1.75} className="text-[#1B6C62]" />
 <span className="label-mono text-[#0D2B52]">{COMPASS_STRATEGIST.dialogue.compassLabel}</span>
 </div>
 <p className="text-[14px] font-serif text-[#1A1A1A] leading-relaxed">
 {COMPASS_STRATEGIST.dialogue.compass}
 </p>
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* INTERACTIVE FEATURE SHOWCASE */}
 <FeatureShowcase />

 {/* WORTH-IT CHECK teaser, pulls the strongest persuasion onto the homepage */}
 <section className="bg-white border-b border-[#E5E4DE]">
 <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-10 text-center">
 <div className="label-mono mb-4 text-[#1B6C62]">What does going it alone really cost?</div>
 <p className="font-serif text-[18px] sm:text-[19px] text-[#1A1A1A] leading-[1.7] max-w-2xl mx-auto mb-5">
 A solicitor&rsquo;s letter runs <strong>$450&ndash;$650</strong>. A tenancy advocate, <strong>$800&ndash;$1,200</strong>. TenantTrail&rsquo;s Dispute Mode is a one-off <strong className="text-[#1B6C62]">$69</strong>.
 </p>
 <Link to="/features#worth-it" className="inline-flex items-center gap-1.5 text-sm text-[#1B6C62] hover:underline font-mono" data-testid="worth-it-teaser-link">
 See the honest, side-by-side numbers <ArrowRight size={13} strokeWidth={1.75} />
 </Link>
 </div>
 </section>

 {/* COMPASS section moved up (above FeatureShowcase). Original placement removed. */}

 {/* FEATURES "move-in to move-out" block removed. Bond safety and clean record are already covered by the six-verb block and Compass. "Family and pets, covered" folded into the STORE pillar. Fair-go / calm-reply is the exact promise of Compass. */}

 {/* BUILT BY A RENTER WHO WON, verbatim owner copy */}
 <section className="bg-[#FAF7F0] border-y border-[#E5E4DE]" data-testid="landing-built-by-renter">
 <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-14">
 <div className="label-mono mb-4">{BUILT_BY_RENTER.label}</div>
 <div className="font-serif text-[18px] sm:text-[19px] text-[#1A1A1A] leading-[1.7] space-y-5">
 {BUILT_BY_RENTER.paragraphs.map((p, i) => (
 <p key={i}>{p}</p>
 ))}
 </div>
 <div className="mt-8 pt-6 border-t border-[#E5E4DE]">
 <Link
 to={BUILT_BY_RENTER.ctaHref}
 className="inline-flex items-center gap-1.5 text-sm text-[#1B6C62] hover:underline font-mono"
 data-testid="landing-founder-link"
 >
 {BUILT_BY_RENTER.ctaLabel} <ArrowRight size={13} strokeWidth={1.75} />
 </Link>
 </div>
 </div>
 </section>

 {/* START TODAY, NOT THE DAY IT GOES WRONG, verbatim owner copy */}
 <section className="bg-[#FAF6E8] border-b border-[#C9A961]/30" data-testid="landing-start-today">
 <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
 <div className="label-mono mb-3 font-bold !text-[#8A6D3B] text-[11.5px] sm:text-[12.5px]">{START_TODAY.label}</div>
 <h2 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] leading-[1.15] mb-5 max-w-xl">
 Start your record today, while it costs you nothing.
 </h2>
 <div className="font-serif text-[18px] sm:text-[19px] text-[#1A1A1A] leading-[1.7] space-y-5">
 {START_TODAY.paragraphs.map((p, i) => (
 <p key={i}>{p}</p>
 ))}
 </div>
 <div className="mt-8">
 <Link
 to={START_TODAY.ctaHref}
 className="btn-teal inline-flex items-center gap-2"
 data-testid="landing-start-today-cta"
 >
 {START_TODAY.ctaLabel} <ArrowRight size={16} strokeWidth={1.5} />
 </Link>
 </div>
 </div>
 </section>

 {/* PRICING PEEK */}
 <section className="bg-[#0D2B52] text-white">
 <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20 text-center">
 <div className="label-mono mb-4 text-[#5EE0CC]">Pricing</div>
 <h2 className="font-serif text-3xl sm:text-4xl mb-4">Start free. Stay free if you don&rsquo;t need more.</h2>
 <p className="text-white/70 max-w-xl mx-auto mb-8">
 Try Compass, your friendly renting helper, with 5 free chats. From $9.99/month if you want bond protection and the monthly photo check-ins. $19.99 for the full kit, that&rsquo;s less than two coffees.
 </p>
 <Link to="/pricing" className="inline-block bg-[#1B6C62] hover:brightness-95 text-white px-7 py-2.5 rounded-sm font-medium transition-all" data-testid="pricing-link">
 See all plans
 </Link>
 <p className="text-[12px] text-white/55 mt-6 max-w-md mx-auto">
 Already in a dispute? <Link to="/pricing" className="underline hover:text-[#5EE0CC]">Dispute Mode, $69 one-off</Link>, 30 days with a generous AI allowance for the tough stuff.
 </p>
 </div>
 </section>

      {/* FAQ, the questions a renter asks before signing up */}
      <section className="bg-white border-t border-[#E5E4DE]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
          <div className="label-mono mb-3">Common questions</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#0D2B52] mb-10">Things people ask before joining.</h2>
          <div className="space-y-2">
            <LandingFaq q="What is Compass?" a="Compass is the friendly name we give our renting helper. Like a savvy mate who happens to know your state's renting rules inside out. Ask Compass anything in plain English: can my landlord enter without notice? Is this rent rise allowed? What do I write back to my agent? Compass answers warmly, points to the right rules. Will help draft your reply if you want." />
            <LandingFaq q="Who is TenantTrail for?" a="Any Australian renter. New tenants who want a tidy record from day one. Renters in the middle of a tough patch with their agent. People heading toward a tribunal hearing. Even pet owners and families who want everything (vet records, kid photos, repair requests) in one safe place." />
            <LandingFaq q="Is it actually free to start?" a="Yes. No card, no trial countdown. You get 5 Compass chats and 250 MB of safe storage on the free plan, forever. Upgrade only if you want more chats, monthly photo check-ins, or the full kit." />
            <LandingFaq q="What if I'm already in a fight with my agent?" a="That's exactly when most people find us. Dispute Mode ($69 one-off) gives you 30 days with 500 AI queries, plenty of room to get organised, write the letters you need and walk into your tribunal day calm and prepared. No subscription." />
            <LandingFaq q="Is this legal advice?" a="No and we'll never pretend otherwise. Compass is a friendly helper, not a lawyer. For actual legal advice your state's Tenants' Union is free and brilliant. Compass will always point you to the right one when it matters." />
            <LandingFaq q="What about my privacy?" a="Your documents are yours. Stored against your account only, never sold, never used to train public AI models. If you ever want everything deleted, one email to support and it's gone." />
            <LandingFaq q="Why &lsquo;TenantTrail&rsquo;?" a="Because every tenancy leaves a trail: photos, emails, payments, conversations. Most renters' trails are scattered across three phones, a Gmail folder and a memory. TenantTrail keeps yours in one safe place, in case you ever need it." />
          </div>
        </div>
      </section>


 {/* DIGITAL FORENSICS FAQ */}
 <ForensicsFAQ />

 <SiteFooter />
 </div>
 );
}


function LandingFaq({ q, a }) {
  return (
    <details className="group border-b border-[#E5E4DE] py-4" data-testid={`landing-faq-${q.slice(0, 24).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <summary className="cursor-pointer flex items-start justify-between gap-3 list-none">
        <span className="font-serif text-[17px] sm:text-lg text-[#0D2B52] leading-snug">{q}</span>
        <span className="shrink-0 text-[#1B6C62] text-xl leading-none group-open:rotate-45 transition-transform tap-target">+</span>
      </summary>
      <p className="mt-3 text-[14px] text-[#5a6b7a] leading-relaxed">{a}</p>
    </details>
  );
}
